/**
 * Cliente del backend.
 *
 * Todo lo que puede salir mal se convierte en un ErrorApi con `codigo`, porque
 * la interfaz no reacciona igual a cada caso: si falta la API key hay que
 * llevar al usuario a conectarla, si Gextia no responde hay que decirle que se
 * reintentará solo, y si es una validación hay que enseñar el mensaje junto al
 * campo. Un `catch` con un string no permite nada de eso.
 */

import { identificador } from './identificador';

export type CodigoError =
  | 'NO_SESSION'
  | 'NO_GEXTIA_KEY'
  | 'GEXTIA_KEY_INVALIDA'
  | 'VALIDACION'
  | 'REGLA_GEXTIA'
  | 'SIN_PERMISO'
  | 'GEXTIA_NO_DISPONIBLE'
  | 'FOTO_INVALIDA'
  | 'ERROR_INTERNO'
  | 'SIN_RED'
  /**
   * Lo que se pedía ya no está en Gextia — normalmente porque alguien lo borró
   * allí. No es un fallo del que se salga reintentando: hay que limpiar la
   * copia local y tirar lo que la cola tuviera pendiente para ese registro.
   */
  | 'YA_NO_EXISTE';

export class ErrorApi extends Error {
  constructor(
    message: string,
    readonly codigo: CodigoError,
    readonly reintentable = false,
  ) {
    super(message);
    this.name = 'ErrorApi';
  }
}

export interface Empleado {
  id: number;
  name: string;
  companyId: number;
  companyName: string;
}

export interface Sesion {
  email: string;
  name: string;
  gextiaConectado: boolean;
  empleado?: Empleado;
  motivo?: string;
}

export interface Categoria {
  id: number;
  name: string;
  code: string | null;
}

export type ModoPago = 'own_account' | 'company_account';

export interface Nota {
  id: number;
  name: string;
  state: string;
  totalAmount: number;
  paymentMode: ModoPago | null;
  expenseIds: number[];
  vendorId: number | null;
  vendorName: string;
  /** Si Gextia va a pedir proveedor para esta nota al procesarla. */
  vendorRequired: boolean;
  approverId: number | null;
  approverName: string;
}

export interface Proveedor {
  id: number;
  name: string;
}

export interface Responsable {
  id: number;
  name: string;
}

/** Lo que se llevaría por delante borrar una nota. */
export interface ResumenBorrado {
  expenses: number;
  photos: number;
  canDelete: boolean;
}

export interface Gasto {
  id: number;
  description: string;
  amount: number;
  date: string;
  categoryId: number | null;
  categoryName: string;
  paymentMode: ModoPago;
  photoCount: number;
}

export interface NotaConGastos extends Nota {
  gastos: Gasto[];
}

export interface Foto {
  id: number;
  filename: string;
  sizeBytes: number;
}

interface Opciones {
  method?: string;
  json?: unknown;
  body?: BodyInit;
  /**
   * Clave de idempotencia. En F2 se genera al enviar; en F3, cuando exista la
   * cola, se generará al encolar para que todos los reintentos la compartan.
   */
  idempotencyKey?: string;
}

async function peticion<T>(path: string, opciones: Opciones = {}): Promise<T> {
  const { method = 'GET', json, body, idempotencyKey } = opciones;

  const cabeceras: Record<string, string> = {};
  if (json !== undefined) cabeceras['Content-Type'] = 'application/json';
  if (idempotencyKey) cabeceras['Idempotency-Key'] = idempotencyKey;

  let respuesta: Response;
  try {
    respuesta = await fetch(path, {
      method,
      headers: cabeceras,
      credentials: 'same-origin',
      body: json !== undefined ? JSON.stringify(json) : body,
    });
  } catch {
    // El móvil sin cobertura entra por aquí. Se marca reintentable para que la
    // cola de F3 lo recoja en vez de descartar la acción.
    throw new ErrorApi('No hay conexión ahora mismo.', 'SIN_RED', true);
  }

  if (respuesta.status === 204) {
    return undefined as T;
  }

  const texto = await respuesta.text();
  const datos = texto ? JSON.parse(texto) : null;

  if (!respuesta.ok) {
    const codigo = (datos?.codigo ?? datos?.code ?? 'ERROR_INTERNO') as CodigoError;

    // La clave revocada tiene que llegar a toda la app, no solo a quien hizo la
    // llamada: si el usuario está en la pantalla de fotos, lo útil es llevarle a
    // reconectar, no dejarle un aviso rojo que no explica qué hacer.
    if (codigo === 'GEXTIA_KEY_INVALIDA') {
      for (const aviso of avisosDeClavePerdida) aviso();
    }

    throw new ErrorApi(datos?.error ?? 'Algo ha fallado.', codigo, datos?.reintentable === true);
  }

  return datos as T;
}

const avisosDeClavePerdida = new Set<() => void>();

/** Avisa cuando Gextia rechaza la credencial guardada, venga de donde venga. */
export function alPerderLaClave(callback: () => void): () => void {
  avisosDeClavePerdida.add(callback);
  return () => avisosDeClavePerdida.delete(callback);
}

export const api = {
  sesion: () => peticion<Sesion>('/api/me'),

  conectarGextia: (apiKey: string) =>
    peticion<{ gextiaConectado: boolean; empleado: Empleado }>('/api/me/gextia-key', {
      method: 'PUT',
      json: { apiKey },
    }),

  desconectarGextia: () =>
    peticion<{ gextiaConectado: boolean }>('/api/me/gextia-key', { method: 'DELETE' }),

  categorias: () => peticion<Categoria[]>('/api/categorias'),

  notas: (historico = false) =>
    peticion<Nota[]>(`/api/notas${historico ? '?historico=1' : ''}`),

  nota: (id: number) => peticion<NotaConGastos>(`/api/notas/${id}`),

  crearNota: (nombre: string, idempotencyKey?: string) =>
    peticion<Nota>('/api/notas', { method: 'POST', json: { nombre }, idempotencyKey }),

  crearGasto: (
    notaId: number,
    datos: {
      concepto: string;
      importe: number;
      categoriaId: number;
      fecha?: string;
      modoPago?: ModoPago;
    },
    idempotencyKey?: string,
  ) =>
    peticion<Gasto>(`/api/notas/${notaId}/gastos`, {
      method: 'POST',
      json: datos,
      idempotencyKey,
    }),

  borrarNota: (id: number, idempotencyKey?: string) =>
    peticion<void>(`/api/notas/${id}`, { method: 'DELETE', idempotencyKey }),

  resumenBorrado: (id: number) => peticion<ResumenBorrado>(`/api/notas/${id}/borrado`),

  actualizarNota: (
    id: number,
    cambios: { proveedorId?: number | null; responsableId?: number | null },
  ) => peticion<Nota>(`/api/notas/${id}`, { method: 'PATCH', json: cambios }),

  proveedores: (q: string) =>
    peticion<Proveedor[]>(`/api/proveedores?q=${encodeURIComponent(q)}`),

  responsables: () => peticion<Responsable[]>('/api/responsables'),

  borrarGasto: (id: number) => peticion<void>(`/api/gastos/${id}`, { method: 'DELETE' }),

  fotos: (gastoId: number) => peticion<Foto[]>(`/api/gastos/${gastoId}/fotos`),

  subirFoto: (gastoId: number, archivo: File, idempotencyKey?: string) => {
    const form = new FormData();
    form.append('foto', archivo);
    return peticion<Foto>(`/api/gastos/${gastoId}/fotos`, {
      method: 'POST',
      body: form,
      idempotencyKey,
    });
  },

  borrarFoto: (id: number) => peticion<void>(`/api/fotos/${id}`, { method: 'DELETE' }),

  enviarNota: (id: number, idempotencyKey?: string) =>
    peticion<Nota>(`/api/notas/${id}/enviar`, { method: 'POST', idempotencyKey }),
};

/** Identificador único para idempotencia. */
export function nuevaClave(): string {
  return identificador();
}

/**
 * Los estados de Gextia, dichos como los diría una persona.
 *
 * `post` es «Contabilizado» en el ERP; aquí se dice «Contabilizada» porque para
 * quien la mandó lo relevante es que ya está en el circuito contable y no hay
 * nada que hacer. El tono (neutro, en curso, cerrado) decide el color.
 */
export const ESTADOS: Record<string, { texto: string; tono: 'espera' | 'ok' | 'neutro' }> = {
  draft: { texto: 'Borrador', tono: 'neutro' },
  submit: { texto: 'Enviada', tono: 'espera' },
  approve: { texto: 'Aprobada', tono: 'espera' },
  post: { texto: 'Contabilizada', tono: 'ok' },
  done: { texto: 'Pagada', tono: 'ok' },
  cancel: { texto: 'Cancelada', tono: 'neutro' },
};

export function estadoLegible(state: string): { texto: string; tono: string } {
  return ESTADOS[state] ?? { texto: state, tono: 'neutro' };
}

export function formatoEuros(valor: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(valor);
}
